// PREZENCE — My Report (employee self-serve, transparency-first)
// Employees can view their own attendance + productivity for any month and
// export it to CSV. Nothing hidden — full transparency into their own data.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const fmtMin = m => { const h = Math.floor(m/60), mm = m%60; return h>0 ? `${h}h ${mm}m` : `${mm}m`; };
const fmtTime = t => t ? new Date(t).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'}) : '—';
const fmtDate = d => new Date(d+'T00:00:00').toLocaleDateString('en-US',{weekday:'short',day:'numeric',month:'short'});

export default function MyReport() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear]   = useState(now.getFullYear());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/reports/my?month=${month}&year=${year}`)
      .then(r => setReport(r.data))
      .catch(() => toast.error('Could not load your report'))
      .finally(() => setLoading(false));
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!report?.daily?.length) { toast.error('Nothing to export for this month'); return; }
    const headers = ['Date','Clock In','Clock Out','Status','Punctuality','Hours','Productivity %','Active (min)','Idle (min)','Break (min)','Meeting (min)'];
    const rows = report.daily.map(d => [
      d.date, fmtTime(d.clock_in), fmtTime(d.clock_out), d.status || '', d.clock_in ? (d.is_late ? 'LATE' : 'ON TIME') : '',
      d.total_hours ?? '', d.productivity ?? '', d.active_minutes, d.idle_minutes, d.break_minutes, d.meeting_minutes,
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `my-report-${year}-${String(month).padStart(2,'0')}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  const s = report?.summary || {};
  const cards = [
    { label:'Days Worked',     value: s.days_worked ?? 0,        color:'#22c55e' },
    { label:'Avg Productivity',value: `${s.avg_productivity ?? 0}%`, color:'#63dcbc' },
    { label:'Late Days',       value: s.late_days ?? 0,          color:'#f59e0b' },
    { label:'Leave Days',      value: s.leave_days ?? 0,         color:'#a78bfa' },
    { label:'Active Time',     value: fmtMin(s.total_active_min ?? 0),  color:'#3ba8ff' },
    { label:'Idle Time',       value: fmtMin(s.total_idle_min ?? 0),    color:'#ef4444' },
  ];

  return (
    <div style={{ padding:'8px 4px' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12, marginBottom:20 }}>
        <div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:26, fontWeight:800, margin:0 }}>My Report</h1>
          <p style={{ color:'var(--text-muted)', fontSize:13, marginTop:4 }}>Your attendance and productivity — full transparency into your own data.</p>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <select className="form-input" value={month} onChange={e=>setMonth(Number(e.target.value))} style={{ width:'auto' }}>
            {MONTHS.map((mn,i) => <option key={i} value={i+1}>{mn}</option>)}
          </select>
          <select className="form-input" value={year} onChange={e=>setYear(Number(e.target.value))} style={{ width:'auto' }}>
            {[year-1, year].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="btn btn-primary" onClick={exportCSV} style={{ whiteSpace:'nowrap' }}>Export CSV</button>
        </div>
      </div>

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:48 }}><div className="spinner" style={{width:28,height:28}} /></div>
      ) : (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:20 }}>
            {cards.map((c,i) => (
              <div key={i} className="card" style={{ padding:16 }}>
                <div style={{ fontSize:22, fontWeight:800, color:c.color, fontFamily:'var(--font-mono)' }}>{c.value}</div>
                <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4, letterSpacing:'0.05em', textTransform:'uppercase' }}>{c.label}</div>
              </div>
            ))}
          </div>

          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', fontSize:13, fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase', color:'var(--text-muted)' }}>
              Daily Breakdown — {MONTHS[month-1]} {year}
            </div>
            {report?.daily?.length ? (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ color:'var(--text-muted)', fontSize:11, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                      {['Date','In','Out','Status','Punctuality','Productivity','Active','Idle','Break','Meeting'].map(h => (
                        <th key={h} style={{ textAlign:'left', padding:'10px 16px', whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.daily.map((d,i) => (
                      <tr key={i} style={{ borderTop:'1px solid var(--border)' }}>
                        <td style={{ padding:'10px 16px', whiteSpace:'nowrap' }}>{fmtDate(d.date)}</td>
                        <td style={{ padding:'10px 16px' }}>{fmtTime(d.clock_in)}</td>
                        <td style={{ padding:'10px 16px' }}>{fmtTime(d.clock_out)}</td>
                        <td style={{ padding:'10px 16px' }}>
                          <span style={{ fontSize:11, padding:'2px 8px', borderRadius:6, textTransform:'capitalize',
                            background: d.status==='late'?'rgba(245,158,11,0.12)':d.status==='present'?'rgba(34,197,94,0.12)':'rgba(148,163,184,0.12)',
                            color: d.status==='late'?'#f59e0b':d.status==='present'?'#22c55e':'var(--text-muted)' }}>{d.status||'—'}</span>
                        </td>
                        <td style={{ padding:'10px 16px' }}>{d.clock_in ? (d.is_late
                          ? <span style={{ fontSize:11, padding:'2px 8px', borderRadius:6, background:'rgba(239,68,68,0.12)', color:'#ef4444', fontWeight:700 }}>⚠ LATE</span>
                          : <span style={{ fontSize:11, padding:'2px 8px', borderRadius:6, background:'rgba(34,197,94,0.12)', color:'#22c55e' }}>ON TIME</span>) : '—'}</td>
                        <td style={{ padding:'10px 16px', fontFamily:'var(--font-mono)', color: d.productivity>=80?'#22c55e':d.productivity>=60?'#f59e0b':d.productivity!=null?'#ef4444':'var(--text-muted)' }}>
                          {d.productivity!=null ? `${d.productivity}%` : '—'}
                        </td>
                        <td style={{ padding:'10px 16px', fontFamily:'var(--font-mono)' }}>{fmtMin(d.active_minutes)}</td>
                        <td style={{ padding:'10px 16px', fontFamily:'var(--font-mono)', color:d.idle_minutes>0?'#ef4444':'var(--text-muted)' }}>{fmtMin(d.idle_minutes)}</td>
                        <td style={{ padding:'10px 16px', fontFamily:'var(--font-mono)' }}>{fmtMin(d.break_minutes)}</td>
                        <td style={{ padding:'10px 16px', fontFamily:'var(--font-mono)' }}>{fmtMin(d.meeting_minutes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ padding:40, textAlign:'center', color:'var(--text-muted)', fontSize:13 }}>
                No attendance records for {MONTHS[month-1]} {year}.
              </div>
            )}
          </div>

          {report?.topApps?.length > 0 && (
            <div className="card" style={{ padding:0, overflow:'hidden', marginTop:16 }}>
              <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', fontSize:13, fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase', color:'var(--text-muted)' }}>
                Most-Used Apps
              </div>
              {report.topApps.map((a,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 20px', borderTop:i?'1px solid var(--border)':'none' }}>
                  <span style={{ fontSize:13 }}>{a.app_name}</span>
                  <span style={{ fontSize:12, fontFamily:'var(--font-mono)', color:'var(--text-muted)' }}>{fmtMin(Math.round(a.seconds/60))}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
